import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { ApiHelper } from '../../../utils/apiHelper';
import axios from "axios";

export const fetchAnalysis = createAsyncThunk(
    "analysis/fetchAnalysis",
    async (startupId) => {
        const response = await ApiHelper.get(`http://dev-kf-bff.ardventure.com.tr/startup/${startupId}/analysis`);
        // API yanıtını uygun formata dönüştür
        const groupedAnalysis = response.data.startupAnalysis.reduce((acc, item) => {
            const type = item.analyzeType;
            if (!acc[type]) {
                acc[type] = [];
            }
            acc[type].push(item);
            return acc;
        }, {});
        return groupedAnalysis;
    }
);

export const updateAnalysis = createAsyncThunk(
    "analysis/updateAnalysis",
    async (analysisData, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.put("/startup/analysis", analysisData);
            if (response.data.code === "00000") {
                toast.success('Analiz bilgileri başarıyla güncellendi');
                return analysisData;
            }
            throw new Error("Failed to update analysis");
        } catch (error) {
            toast.error('Analiz bilgileri güncellenirken bir hata oluştu');
            return rejectWithValue(error.message);
        }
    }
);

export const deleteAnalysisItem = createAsyncThunk(
    "analysis/deleteAnalysisItem",
    async ({ analyzeId }, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.delete("/startup/analysis", {
                data: { analyzeId}
            });
            if (response.data.code === "00000") {
                toast.success('Analiz maddesi başarıyla silindi');
                return { analyzeId };
            }
            throw new Error("Failed to delete analysis item");
        } catch (error) {
            toast.error('Analiz maddesi silinirken bir hata oluştu');
            return rejectWithValue(error.message);
        }
    }
);

export const createAnalysis = createAsyncThunk(
    "analysis/createAnalysis",
    async (analysisData, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.post("/startup/analysis", analysisData);
            if (response.data.code === "00000") {
                toast.success("Analiz başarıyla oluşturuldu");
                return response.data.analysis;
            } else {
                throw new Error("Analysis creation failed");
            }
        } catch (error) {
            toast.error("Analiz oluşturulurken bir hata oluştu");
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const deleteAnalysis = createAsyncThunk(
    "analysis/deleteAnalysis",
    async (analyzeId, { rejectWithValue }) => {
        try {
            const response = await ApiHelper.delete('/startup/analysis', {
                data: { analyzeId }
            });
            if (response.data.code === "00000") {
                toast.success("Analiz başarıyla silindi");
                return analyzeId;
            } else {
                throw new Error("Analysis deletion failed");
            }
        } catch (error) {
            toast.error("Analiz silinirken bir hata oluştu");
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const fetchAnalyzeDocuments = createAsyncThunk(
    'analysis/fetchAnalyzeDocuments',
    async (startupId) => {
        const response = await ApiHelper.get(`/startup/${startupId}/analyze-documents`);
        console.log("Analyze Documents:", response.data.startupAnalyzeDocuments);
        return response.data.startupAnalyzeDocuments[0];
    }
);

export const updateAnalyzeDocuments = createAsyncThunk(
    'analysis/updateAnalyzeDocuments',
    async ({ startupId, swotAnalysisPath, businessPlanPath, investorPresentationPath }) => {
        // Boş olmayan path'leri içeren yeni bir obje oluştur
        const payload = { startupId };
        
        if (swotAnalysisPath) {
            payload.swotAnalysisPath = swotAnalysisPath;
        }
        
        if (businessPlanPath) {
            payload.businessPlanPath = businessPlanPath;
        }
        
        if (investorPresentationPath) {
            payload.investorPresentationPath = investorPresentationPath;
        }

        const response = await ApiHelper.put('/startup/analyze-documents', payload);
        return response.data;
    }
);

const analysisSlice = createSlice({
    name: "analysis",
    initialState: {
        analysisByType: {},
        loading: false,
        error: null,
        analyzeDocuments: null
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchAnalysis.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchAnalysis.fulfilled, (state, action) => {
                state.loading = false;
                state.analysisByType = action.payload;
            })
            .addCase(fetchAnalysis.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(updateAnalysis.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateAnalysis.fulfilled, (state, action) => {
                state.loading = false;
                const { analysisType, description } = action.payload;
                if (!state.analysisByType[analysisType]) {
                    state.analysisByType[analysisType] = [];
                }
                state.analysisByType[analysisType].push({
                    analysisType,
                    description
                });
            })
            .addCase(updateAnalysis.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(deleteAnalysisItem.fulfilled, (state, action) => {
                const { analysisId } = action.payload;
                Object.keys(state.analysisByType).forEach(type => {
                    state.analysisByType[type] = state.analysisByType[type].filter(
                        item => item.id !== analysisId
                    );
                });
            })
            .addCase(createAnalysis.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                if (!state.analysisByType[action.payload.analysisType]) {
                    state.analysisByType[action.payload.analysisType] = [];
                }
                state.analysisByType[action.payload.analysisType].push(action.payload);
            })
            .addCase(createAnalysis.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(deleteAnalysis.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                Object.keys(state.analysisByType).forEach(type => {
                    state.analysisByType[type] = state.analysisByType[type].filter(
                        item => item.id !== action.payload
                    );
                });
            })
            .addCase(deleteAnalysis.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchAnalyzeDocuments.fulfilled, (state, action) => {
                state.analyzeDocuments = action.payload;
                state.loading = false;
            })
            .addCase(fetchAnalyzeDocuments.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchAnalyzeDocuments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(updateAnalyzeDocuments.fulfilled, (state, action) => {
                state.loading = false;
                state.analyzeDocuments = action.payload;
            })
            .addCase(updateAnalyzeDocuments.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateAnalyzeDocuments.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            });
    }
});

export default analysisSlice.reducer; 